import {z} from 'zod';
import {zDateTime, zNumberToString} from '@brunata-angular/util';

export const deliveryOutcomes = ['DELIVERED', 'FAILED'] as const;

export type DeliveryOutcome = (typeof deliveryOutcomes)[number];

export const deliveryOutcomeLabelKeys: Record<DeliveryOutcome, string> = {
    DELIVERED: 'savedexport_deliverylog_outcome_delivered',
    FAILED: 'savedexport_deliverylog_outcome_failed',
};

export const deliveryOutcomeIconClasses: Record<DeliveryOutcome, string> = {
    DELIVERED: 'bi bi-check-circle-fill text-success',
    FAILED: 'bi bi-exclamation-circle-fill text-warning',
};

export const deliveryFailureReasons = [
    'SERVER_UNREACHABLE',
    'LOGIN_REJECTED',
    'SERVER_KEY_CHANGED',
    'FOLDER_NOT_FOUND',
    'WRITE_REJECTED',
    'CONNECTION_MISSING',
    'NO_RECIPIENTS',
    'FILE_TOO_LARGE',
    'INTERRUPTED',
    'OTHER',
] as const;

export type DeliveryFailureReason = (typeof deliveryFailureReasons)[number];

export const deliveryFailureReasonLabelKeys: Record<DeliveryFailureReason, string> = {
    SERVER_UNREACHABLE: 'savedexport_deliverylog_reason_server_unreachable',
    LOGIN_REJECTED: 'savedexport_deliverylog_reason_login_rejected',
    SERVER_KEY_CHANGED: 'savedexport_deliverylog_reason_server_key_changed',
    FOLDER_NOT_FOUND: 'savedexport_deliverylog_reason_folder_not_found',
    WRITE_REJECTED: 'savedexport_deliverylog_reason_write_rejected',
    CONNECTION_MISSING: 'savedexport_deliverylog_reason_connection_missing',
    NO_RECIPIENTS: 'savedexport_deliverylog_reason_no_recipients',
    FILE_TOO_LARGE: 'savedexport_deliverylog_reason_file_too_large',
    INTERRUPTED: 'savedexport_deliverylog_reason_interrupted',
    OTHER: 'savedexport_deliverylog_reason_other',
};

export const deliveryFailureReasonShortLabelKeys: Record<DeliveryFailureReason, string> = {
    SERVER_UNREACHABLE: 'savedexport_deliverylog_reason_short_server_unreachable',
    LOGIN_REJECTED: 'savedexport_deliverylog_reason_short_login_rejected',
    SERVER_KEY_CHANGED: 'savedexport_deliverylog_reason_short_server_key_changed',
    FOLDER_NOT_FOUND: 'savedexport_deliverylog_reason_short_folder_not_found',
    WRITE_REJECTED: 'savedexport_deliverylog_reason_short_write_rejected',
    CONNECTION_MISSING: 'savedexport_deliverylog_reason_short_connection_missing',
    NO_RECIPIENTS: 'savedexport_deliverylog_reason_short_no_recipients',
    FILE_TOO_LARGE: 'savedexport_deliverylog_reason_short_file_too_large',
    INTERRUPTED: 'savedexport_deliverylog_reason_short_interrupted',
    OTHER: 'savedexport_deliverylog_reason_short_other',
};

const exportNowSlotPattern = /T\d{2}:\d{2}:\d{2}$/;

export const deliveryLogEntrySchema = z
    .object({
        savedExportId: zNumberToString,
        exportName: z.string().nullable().default(null),
        scheduledFor: z.string(),
        outcome: z.enum(deliveryOutcomes),
        deliveredAt: zDateTime,
        destination: z.string().nullable().default(null),
        fileName: z.string().nullable().default(null),
        bytes: z.number().nullable().default(null),
        durationMs: z.number().nullable().default(null),
        failureReason: z.enum(deliveryFailureReasons).nullable().catch(null),
        notifiedTo: z.array(z.string()).optionalUntilDeployed('AGI-20206'),
        attempts: z.number().optionalUntilDeployed('AGI-18212'),
    })
    .transform(({durationMs, failureReason, notifiedTo, attempts, ...entry}) => ({
        ...entry,
        attempts: attempts ?? 1,
        fromExportNow: exportNowSlotPattern.test(entry.scheduledFor),
        durationSeconds: durationMs === null ? null : durationMs / 1000,
        failureReason: entry.outcome === 'FAILED' ? (failureReason ?? 'OTHER') : null,
        notifiedAddresses: (notifiedTo ?? []).join(', '),
    }));

export type DeliveryLogEntry = z.infer<typeof deliveryLogEntrySchema>;

export const deliveryLogPageSchema = z.object({
    totalCount: z.number(),
    deliveries: z.array(deliveryLogEntrySchema).default([]),
});

export type DeliveryLogPage = z.infer<typeof deliveryLogPageSchema>;
